import React, { useEffect, useState } from "react";
import { Plus, Star, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { HandshakeIcon } from "../HandshakeIcon";

const UPG_META = {
  member_capacity: { img: "/promotion_upgrades/upgrade_member_capacity.webp", color: "#FFE600" },
  underdog_bonus:  { img: "/promotion_upgrades/upgrade_underdog_bonus.webp",  color: "#FF007F" },
  daily_handshake: { img: "/promotion_upgrades/upgrade_daily_handshake.webp", color: "#00F0FF" },
};

export function PromoXpBar({ p }) {
  const pct = Math.min(100, ((p.xp || 0) / (p.xp_needed || 1)) * 100);
  return (
    <div data-testid="promotion-xp-bar">
      <div className="flex justify-between items-end text-[10px] font-mono uppercase tracking-[0.2em] text-[#C9BDF0] mb-1">
        <span className="flex items-center gap-2">
          <span className="font-graffiti text-lg leading-none text-[#39FF14] tracking-normal" style={{ textShadow: "0 0 10px #39FF1499" }} data-testid="promotion-level">LV {p.level || 1}</span>
          Promotion XP
        </span>
        <span className="lb-num font-bold text-[#39FF14]" data-testid="promotion-xp-text">{p.xp || 0}/{p.xp_needed}</span>
      </div>
      <div className="h-2.5 rounded-full bg-[#2D1C52] overflow-hidden">
        <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #39FF14, #00F0FF)", boxShadow: "0 0 10px #39FF14AA" }} />
      </div>
    </div>
  );
}

const EFFECT = {
  member_capacity: (v) => `+${v} member slots`,
  underdog_bonus:  (v) => `+${v}% Underdog Bonus for all members`,
  daily_handshake: (v) => (v ? `${v} daily Handshake${v > 1 ? "s" : ""} per member` : "Locked"),
};

function UpgradeTooltip({ id, def, lvl, maxed }) {
  const fx = EFFECT[id];
  return (
    <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-30 w-52 rounded-lg border-2 bg-[#0A051A]/95 backdrop-blur px-3 py-2 text-left opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-200"
         style={{ borderColor: UPG_META[id].color }} data-testid={`promo-upgrade-tooltip-${id}`}>
      <div className="font-heading text-xs uppercase tracking-wider" style={{ color: UPG_META[id].color }}>{def.name}</div>
      <div className="text-[10px] font-mono text-[#C9BDF0] mt-0.5">Level {lvl}{def.max != null ? ` / ${def.max}` : ""}</div>
      <div className="text-[11px] font-mono text-white mt-1">Now: {fx(lvl * def.step)}</div>
      <div className="text-[11px] font-mono mt-0.5" style={{ color: maxed ? "#FFE600" : "#39FF14" }}>{maxed ? "Maxed out" : `Next: ${fx((lvl + 1) * def.step)}`}</div>
    </div>
  );
}

function UpgradeTile({ id, def, lvl, canBuy, onBuy }) {
  const m = UPG_META[id];
  const maxed = def.max != null && lvl >= def.max;
  return (
    <div className="group relative w-16 sm:w-[72px] shrink-0" data-testid={`promo-upgrade-${id}`}>
      <UpgradeTooltip id={id} def={def} lvl={lvl} maxed={maxed} />
      <div className="relative aspect-square rounded-lg overflow-hidden border-2 bg-[#0A051A]" style={{ borderColor: m.color + "99", boxShadow: `0 0 10px ${m.color}44` }}>
        <img src={m.img} alt={def.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
        <div className="absolute bottom-0.5 left-0.5 px-1 rounded bg-black/75 font-num text-[11px] leading-tight" style={{ color: m.color }} data-testid={`promo-upgrade-level-${id}`}>
          {lvl}{def.max != null ? `/${def.max}` : ""}
        </div>
        {maxed && <div className="absolute top-0.5 right-0.5 px-1 rounded bg-[#FFE600] text-[#0A051A] text-[8px] font-heading">MAX</div>}
      </div>
      {canBuy && !maxed && (
        <button onClick={() => onBuy(id)} data-testid={`promo-upgrade-btn-${id}`} aria-label={`Upgrade ${def.name}`}
                className="absolute -top-1.5 -right-1.5 z-10 w-6 h-6 rounded-full flex items-center justify-center text-[#0A051A] promo-daily-pulse transition-transform hover:scale-125 active:scale-90"
                style={{ background: m.color, boxShadow: `0 0 12px ${m.color}` }}>
          <Plus size={14} strokeWidth={3.5} />
        </button>
      )}
    </div>
  );
}

export function PromoLevelBadge({ level }) {
  return (
    <div className="relative mx-auto mt-4 w-fit z-20 flex items-center gap-1.5 pl-1 pr-3 py-1 rounded-full border-2 bg-[#0A051A] whitespace-nowrap"
         style={{ borderColor: "#FFE600", boxShadow: "0 0 16px #fff781" }} data-testid="promotion-level-badge">
      <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(140deg, #FFE600, #FF007F)", boxShadow: "0 0 10px #FFE600" }}>
        <Star size={15} strokeWidth={2.8} fill="#0A051A" className="text-[#0A051A]" />
      </span>
      <span className="font-graffiti text-lg leading-none text-white" style={{ textShadow: "0 0 10px #FFE600" }}>LV {level}</span>
    </div>
  );
}

export function PromoUpgrades({ p, isLeader, onRefresh }) {
  const pts = p.upgrade_points || 0;
  const buy = async (id) => {
    try {
      await api.post(`/promotions/${p.promotion_id}/upgrade`, { upgrade_id: id });
      toast.success(`${p.upgrade_defs[id].name} upgraded!`);
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Upgrade failed"); }
  };
  return (
    <div className="wr-rise col-span-3 rounded-xl p-[2px]" style={{ background: "linear-gradient(140deg, #39FF14, #5B3BFF55, #00F0FF66)", animationDelay: "320ms" }} data-testid="promotion-upgrades">
      <div className="h-full rounded-[10px] bg-[#0A051A]/85 backdrop-blur px-2.5 py-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-mono text-[#C9BDF0]">
            <Zap size={14} strokeWidth={2.6} className="text-[#39FF14]" /> Upgrades
          </span>
          <span className="text-[10px] font-mono text-[#39FF14]" data-testid="promotion-upgrade-points">{pts} pt{pts === 1 ? "" : "s"}</span>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          {Object.entries(p.upgrade_defs || {}).map(([id, def]) => (
            <UpgradeTile key={id} id={id} def={def} lvl={p.upgrades?.[id] || 0} canBuy={isLeader && pts > 0} onBuy={buy} />
          ))}
        </div>
      </div>
    </div>
  );
}

const STROKE = "drop-shadow(1px 0 0 #000) drop-shadow(-1px 0 0 #000) drop-shadow(0 1px 0 #000) drop-shadow(0 -1px 0 #000)";

function DailyBadge({ testId, color, onClick, children }) {
  const [burst, setBurst] = useState(false);
  const click = async () => { if (await onClick()) setBurst(true); };
  return (
    <button data-testid={testId} onClick={click} disabled={burst}
            className={`relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center transition-transform hover:scale-110 ${burst ? "promo-daily-claimed" : "promo-daily-pulse"}`}
            style={{ filter: `drop-shadow(0 0 10px ${color}) drop-shadow(0 0 22px ${color}88)` }}>
      <img src="/promotion_upgrades/claim_star.webp" alt="" className="absolute inset-0 w-full h-full object-contain pointer-events-none promo-star-spin" />
      <span className="relative flex flex-col items-center justify-center gap-0.5 font-graffiti text-base sm:text-lg leading-none text-white whitespace-nowrap"
            style={{ WebkitTextStroke: "1.2px #000", textShadow: `0 2px 0 #000, 0 0 10px ${color}` }}>
        {children}
      </span>
      {burst && <span className="promo-daily-float absolute left-1/2 -top-2 flex items-center gap-1 font-graffiti text-xl whitespace-nowrap" style={{ color, WebkitTextStroke: "1px #000", textShadow: `0 0 10px ${color}` }}>{children}</span>}
    </button>
  );
}

export function PromoDailyBadges({ p, onRefresh }) {
  const [d, setD] = useState(null);
  const load = () => api.get(`/promotions/${p.promotion_id}/daily`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [p.promotion_id]);
  const claim = (path, msg) => async () => {
    try {
      await api.post(`/promotions/${p.promotion_id}/${path}`);
      toast.success(msg);
      setTimeout(() => { load(); onRefresh(); }, 1100);
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Claim failed"); load(); return false; }
  };
  if (!d || (!d.xp_claimable && !d.hs_claimable)) return null;
  return (
    <div className="absolute top-2 right-2 z-20 flex gap-1" data-testid="promotion-daily-badges">
      {d.hs_claimable && (
        <DailyBadge testId="promo-daily-hs-btn" color="#00F0FF" onClick={claim("claim-daily-handshakes", `+${d.hs_amount} Handshake${d.hs_amount > 1 ? "s" : ""} claimed!`)}>
          <span style={{ filter: STROKE }}><HandshakeIcon className="w-6 h-6 sm:w-7 sm:h-7" /></span><span>{d.hs_amount}x</span>
        </DailyBadge>
      )}
      {d.xp_claimable && (
        <DailyBadge testId="promo-daily-xp-btn" color="#FFE600" onClick={claim("claim-daily-xp", `+${d.xp_amount} Promotion XP!`)}>
          <span>+{d.xp_amount} XP</span>
        </DailyBadge>
      )}
      <style>{`
        @keyframes promoPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.14); } }
        @keyframes promoClaimed { 0% { transform: scale(1); } 30% { transform: scale(1.45) rotate(-6deg); filter: brightness(2); } 100% { transform: scale(0); opacity: 0; } }
        @keyframes promoFloat { 0% { transform: translate(-50%, 0) scale(1); opacity: 1; } 100% { transform: translate(-50%, -60px) scale(1.6); opacity: 0; } }
        @keyframes promoStarSpin { 0%,100% { transform: rotate(-6deg); } 50% { transform: rotate(6deg); } }
        .promo-star-spin { animation: promoStarSpin 3s ease-in-out infinite; }
        .promo-daily-pulse { animation: promoPulse 1.4s ease-in-out infinite; }
        .promo-daily-claimed { animation: promoClaimed 0.9s ease-out forwards; }
        .promo-daily-float { animation: promoFloat 1s ease-out forwards; pointer-events: none; }
      `}</style>
    </div>
  );
}
